import React, { useState, useEffect, useCallback, useRef } from 'react';
import { loanContractsService, suppliersService, bankAccountsService, recurringExpensesService, expensesService } from '../services/api';
import { fmtDate, addMonthsStr, getTodayStr, getDaysUntil } from '../utils/formatters';
import CascaDeModal from './shared/CascaDeModal';
import {
  CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaDinheiro, CelulaAcoes,
  CabecalhoInterno, CartaoDoTopo,
} from './shared/lista/PecasDaLista';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { campo, campoSomenteLeitura, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, mensagemDoErroDeCarga } from '../design';
import { porqueNaoQuitar, confirmacaoDeExcluirContrato, confirmacaoDeDesativarRecorrente, confirmacaoDasDespesasDoBordero, avisoDasDespesasDoBordero, lerParcelaVencida, fraseDaParcelaVencida, ROTULO_DA_PARCELA, LeituraDaParcela } from '../utils/emprestimos';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Caractere não-ASCII como TEXTO SOLTO no JSX pode ser gravado como escape e
// renderizar literal na tela. Declarar como constante e referenciar por chaves.
const TRACO = '—';        // em-dash, o vazio padrão do HUB

const statusLabels: Record<string, string> = { ativo: 'Ativo', quitado: 'Quitado', renegociado: 'Renegociado' };

// Classe LITERAL COMPLETA, nunca `bg-${cor}-500/20`.
// O Tailwind varre o código em busca de nomes de classe inteiros e não tem
// safelist neste projeto: classe montada em runtime não é gerada no CSS de
// produção. O badge de status desta tela estava exatamente assim — ou seja, já
// vinha renderizando sem cor nenhuma, deixando o estado do contrato dependendo
// só do texto.
// Tons do domínio (11/10/2026): quitado é concluído (era o azul).
const statusChip: Record<string, string> = {
  ativo: TONE_BADGE.open,
  quitado: TONE_BADGE.success,
  renegociado: TONE_BADGE.warning,
};
const statusChipFallback = 'bg-slate-500/20 text-slate-300';

type EstadoBordero = {
  chave: 'liquidado' | 'aberto' | 'sem_titulos';
  rotulo: string;
  chip: string;
  detalhe: string;
};

/**
 * Estado do borderô é DERIVADO, não é o campo `status` do contrato.
 *
 * Nenhum caminho do código escreve `status = quitado` em borderô: o
 * `syncPaidInstallments` só roda pelo botão "Sincronizar Parcelas", que a tela
 * não oferece para borderô, e o `syncLinkedLoanContract` dispara a partir de
 * despesa, que borderô não tem (só as taxas). Resultado medido em 07/08/2026: os
 * 4 borderôs 100% liquidados apareciam "Ativo" com `paidInstallments = 0`, e o
 * único "Quitado" era o C639301270 — que não tem título casado nenhum e por isso
 * marcava 0%. A tela mostrava exatamente o inverso da verdade nos dois lados.
 *
 * Derivar elimina a classe inteira do problema: não há o que sincronizar nem o
 * que ficar velho. O campo `status` do banco segue valendo para empréstimo.
 *
 * "Sem títulos" é um terceiro estado de propósito. Borderô sem lista de
 * duplicatas importada não é liquidado nem em aberto — o sistema não sabe, e
 * fingir que sabe foi o que colocou R$ 34.679,62 no card de pendente.
 */
const estadoBordero = (contrato: any, resumo: any): EstadoBordero => {
  const titulos = Array.isArray(contrato?.discountedReceivables) ? contrato.discountedReceivables.length : 0;
  if (titulos === 0) {
    return {
      chave: 'sem_titulos',
      rotulo: 'Sem títulos',
      chip: 'bg-slate-500/20 text-slate-300',
      detalhe: 'Importe o PDF para acompanhar',
    };
  }
  const restante = Number(resumo?.remaining) || 0;
  if (restante <= 0.01) {
    return {
      chave: 'liquidado',
      rotulo: 'Liquidado',
      // E1 (padrão das listas no Financeiro): liquidado é o desfecho normal, neutro; a cor fica para o que pede ação.
      chip: TONE_BADGE.neutral,
      detalhe: `${titulos} títulos pagos`,
    };
  }
  return {
    chave: 'aberto',
    rotulo: 'Em aberto',
    chip: TONE_BADGE.warning,
    detalhe: `R$ ${fmt(restante)} a liquidar`,
  };
};

type ProximaParcela = {
  numero: number;
  total: number;
  dueDate: string;
  amount: number;
  estado: 'carencia' | 'vencida' | 'a_vencer' | 'quitado';
};

/**
 * Próxima parcela a debitar, derivada do contrato + do resumo do backend.
 *
 * Convenção do cronograma (a mesma do backend): o slot n vence em `startDate` +
 * (n − 1) meses, os `gracePeriodMonths` primeiros são carência e o resto são
 * prestações. Logo a próxima prestação é o slot `carência + pagas + 1`.
 *
 * Não busca o cronograma na API de propósito: seriam 7 requisições no load da
 * aba para exibir uma coluna, e todo o dado necessário já veio no /summary.
 */
const calcProximaParcela = (contrato: any, resumo: any): ProximaParcela | null => {
  const start = String(contrato?.startDate || '').split('T')[0];
  if (!start) return null;

  const carencia = Number(contrato.gracePeriodMonths) || 0;
  const prestacoes = Number(resumo?.payableInstallments ?? (Number(contrato.totalInstallments) || 0) - carencia);
  const pagas = Number(resumo?.paidInstallments ?? contrato.paidInstallments) || 0;
  if (prestacoes <= 0) return null;
  if (pagas >= prestacoes) return { numero: prestacoes, total: prestacoes, dueDate: '', amount: 0, estado: 'quitado' };

  const slot = carencia + pagas + 1;
  const dueDate = addMonthsStr(start, slot - 1);
  const hoje = getTodayStr();

  // Em carência o valor exibido é o da 1a prestação, mas o estado avisa que
  // ainda não há débito — era isso que a tela lia como "0/48" com barra vazia.
  if (resumo?.inGrace) {
    return {
      numero: pagas + 1,
      total: prestacoes,
      dueDate: resumo.graceUntil || dueDate,
      amount: Number(resumo?.installmentAmount ?? contrato.installmentAmount) || 0,
      estado: 'carencia',
    };
  }

  return {
    numero: pagas + 1,
    total: prestacoes,
    dueDate,
    amount: Number(resumo?.installmentAmount ?? contrato.installmentAmount) || 0,
    estado: dueDate < hoje ? 'vencida' : 'a_vencer',
  };
};

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    plus: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' /></svg>,
    edit: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' /></svg>,
    trash: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' /></svg>,
    upload: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' /></svg>,
    download: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' /></svg>,
    refresh: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' /></svg>,
    x: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>,
    eye: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' /><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' /></svg>,
    list: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 12h16M4 18h7' /></svg>,
  };
  return icons[name] || null;
};

type LoanModalCtx = {
  showToast: (message: string, type?: 'success' | 'error' | 'warning') => void;
  schedule: any; bankAccounts: any; suppliers: any; recurringExpenses: any; summary: any;
  /** A base (fornecedores, contas, recorrentes) que não carregou; o form diz e trava os selects. */
  baseErro: string | null;
  /** O cronograma do detalhe que não carregou. */
  scheduleErro: string | null;
  loadContracts: any; loadSummary: any; handleDownloadContract: any; setUploadModal: any;
  handleSync: any; handleReprocessInvoices: any; handleCreateBorderoExpenses: any;
};

  const ContractForm = ({ onClose, contract , ctx }: { onClose: () => void; contract?: any; ctx: LoanModalCtx }) => {
  const { showToast, schedule, loadContracts, loadSummary, handleDownloadContract, bankAccounts, suppliers, setUploadModal, recurringExpenses, handleSync, handleReprocessInvoices, handleCreateBorderoExpenses } = ctx;
    // D2 (11/10/2026): o form vinha da linha da lista e o Salvar mandava o DTO inteiro, com o status e o saldo do credor
    // da foto da tela. Agora relê o contrato ao abrir, e o Salvar fica travado até a leitura voltar.
    const formDe = (contract: any) => ({
      contractNumber: contract?.contractNumber || '',
      description: contract?.description || '',
      supplierId: contract?.supplierId || '',
      bankAccountId: contract?.bankAccountId || '',
      originalAmount: contract?.originalAmount || '',
      installmentAmount: contract?.installmentAmount || '',
      totalInstallments: contract?.totalInstallments || '',
      paidInstallmentsBeforeHub: contract?.paidInstallmentsBeforeHub || 0,
      gracePeriodMonths: contract?.gracePeriodMonths || 0,
      interestRateAnnual: contract?.interestRateAnnual || '',
      interestRateMonthly: contract?.interestRateMonthly || '',
      startDate: contract?.startDate ? String(contract.startDate).split('T')[0] : '',
      endDate: contract?.endDate ? String(contract.endDate).split('T')[0] : '',
      recurringExpenseId: contract?.recurringExpenseId || '',
      sourceBalance: contract?.sourceBalance ?? '',
      sourceBalanceDate: contract?.sourceBalanceDate ? String(contract.sourceBalanceDate).split('T')[0] : '',
      notes: contract?.notes || '',
      status: contract?.status || 'ativo',
    });
    const [form, setForm] = useState(formDe(contract));
    const [lido, setLido] = useState<any>(contract || null);
    const [leitura, setLeitura] = useState<'lendo' | 'ok' | string>(contract ? 'lendo' : 'ok');
    useEffect(() => {
      if (!contract?.id) return;
      let vivo = true;
      loanContractsService.getById(contract.id).then(
        (atual: any) => { if (!vivo) return; setLido(atual); setForm(formDe(atual)); setLeitura('ok'); },
        (e: any) => { if (vivo) setLeitura(mensagemDoErroDeCarga(e)); },
      );
      return () => { vivo = false; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contract?.id]);
    const resumoDoLido = ctx.summary?.contracts?.find((sc: any) => sc.id === contract?.id) || null;
    // D1: "Quitado" à mão com prestação em aberto trava (o card soma só `ativo`).
    const travaDoQuitado = porqueNaoQuitar(lido, resumoDoLido, form.status);
    const porqueNaoSalvar = leitura === 'lendo'
      ? 'Relendo o contrato: o Salvar libera quando a leitura voltar.'
      : leitura !== 'ok'
        ? `Não consegui reler o contrato (${leitura}). Salvar agora gravaria a cópia da lista por cima do que está no servidor.`
        : travaDoQuitado;
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (porqueNaoSalvar) { showToast(porqueNaoSalvar, 'warning'); return; }
      if (!form.contractNumber || !form.description || !form.originalAmount || !form.installmentAmount || !form.totalInstallments || !form.startDate) {
        showToast('Preencha os campos obrigatórios', 'error');
        return;
      }
      // Saldo sem data é saldo que nunca envelhece: daqui a um ano ninguém saberia
      // que aquele número é de hoje. A data é o que permite avisar que ficou velho.
      if (form.sourceBalance !== '' && !form.sourceBalanceDate) {
        showToast('Informe a data da consulta do saldo', 'error');
        return;
      }
      setSaving(true);
      try {
        const payload = {
          ...form,
          supplierId: form.supplierId ? Number(form.supplierId) : null,
          bankAccountId: form.bankAccountId ? Number(form.bankAccountId) : null,
          recurringExpenseId: form.recurringExpenseId ? Number(form.recurringExpenseId) : null,
          originalAmount: parseFloat(String(form.originalAmount)),
          installmentAmount: parseFloat(String(form.installmentAmount)),
          totalInstallments: Number(form.totalInstallments),
          paidInstallmentsBeforeHub: Number(form.paidInstallmentsBeforeHub) || 0,
          // String vazia vira null de propósito: apagar o campo tem que VOLTAR
          // para a projeção, não gravar saldo zero (que significaria quitado).
          sourceBalance: form.sourceBalance === '' || form.sourceBalance === null
            ? null : parseFloat(String(form.sourceBalance)),
          sourceBalanceDate: form.sourceBalanceDate || null,
          gracePeriodMonths: Number(form.gracePeriodMonths) || 0,
          interestRateAnnual: form.interestRateAnnual ? parseFloat(String(form.interestRateAnnual)) : null,
          interestRateMonthly: form.interestRateMonthly ? parseFloat(String(form.interestRateMonthly)) : null,
          endDate: form.endDate || null,
          notes: form.notes || null,
        };
        if (contract) {
          await loanContractsService.update(contract.id, payload);
          showToast('Contrato atualizado');
        } else {
          await loanContractsService.create(payload);
          showToast('Contrato criado');
        }
        await Promise.all([loadContracts(), loadSummary()]);
        onClose();
      } catch (e: any) {
        showToast(`Não salvei o contrato: ${e?.response?.data?.message || mensagemDoErroDeCarga(e)}`, 'error');
      } finally {
        setSaving(false);
      }
    };

    const inputClass = `w-full ${campo} rounded-lg px-3 py-2 text-sm`;
    const labelClass = 'block text-sm font-medium text-slate-300 mb-1';

    return (
      // Formulário: não fecha ao clicar fora (como já era). O botão de envio vive
      // no rodapé da casca, fora do <form>, e o liga pelo atributo `form`; o Enter
      // dentro dos campos continua enviando.
      <CascaDeModal
        largura="base"
        titulo={contract ? 'Editar Contrato' : 'Novo Contrato de Empréstimo'}
        subtitulo={contract ? contract.contractNumber : undefined}
        onClose={onClose}
        corpoClassName="p-6"
        rodape={
          <div className="ml-auto flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancelar</button>
            <button type="submit" form="form-contrato-emprestimo" disabled={saving || !!porqueNaoSalvar} title={porqueNaoSalvar || undefined} className="px-6 py-2 bg-primary hover:bg-primary-strong text-white text-sm font-medium rounded-lg disabled:opacity-50">
              {saving ? 'Salvando...' : contract ? 'Salvar' : 'Criar Contrato'}
            </button>
          </div>
        }
      >
          <form id="form-contrato-emprestimo" onSubmit={handleSubmit} className="space-y-4">
            {leitura !== 'ok' && (
              <p role="status" className={`text-sm ${leitura === 'lendo' ? 'text-slate-400' : TONE_TEXT.warning}`} data-testid="releitura-do-contrato">{porqueNaoSalvar}</p>
            )}
            {ctx.baseErro && (
              <p role="alert" className={`text-sm ${TONE_TEXT.warning}`} data-testid="base-que-nao-carregou">
                Não consegui ler fornecedores, contas e despesas recorrentes ({ctx.baseErro}): os vínculos atuais foram mantidos e os campos ficam travados.
              </p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>N. Contrato *</label>
                <input className={inputClass} value={form.contractNumber} onChange={e => setForm({ ...form, contractNumber: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Status</label>
                <select className={inputClass} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="ativo">Ativo</option>
                  <option value="quitado">Quitado</option>
                  <option value="renegociado">Renegociado</option>
                </select>
                {travaDoQuitado && <p className={`mt-1 text-xs ${TONE_TEXT.warning}`} data-testid="trava-do-quitado">{travaDoQuitado}</p>}
              </div>
            </div>
            <div>
              <label className={labelClass}>Descrição *</label>
              <input className={inputClass} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Instituição Financeira</label>
                <select className={ctx.baseErro ? `w-full ${campoSomenteLeitura} rounded-lg px-3 py-2 text-sm` : inputClass} disabled={!!ctx.baseErro} value={form.supplierId} onChange={e => setForm({ ...form, supplierId: e.target.value })}>
                  <option value="">Selecione...</option>
                  {suppliers.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Conta Bancária</label>
                <select className={ctx.baseErro ? `w-full ${campoSomenteLeitura} rounded-lg px-3 py-2 text-sm` : inputClass} disabled={!!ctx.baseErro} value={form.bankAccountId} onChange={e => setForm({ ...form, bankAccountId: e.target.value })}>
                  <option value="">Selecione...</option>
                  {bankAccounts.map((b: any) => <option key={b.id} value={b.id}>{b.bankName} - {b.accountNumber}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Valor Original *</label>
                <input type="number" step="0.01" className={inputClass} value={form.originalAmount} onChange={e => setForm({ ...form, originalAmount: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Valor Parcela *</label>
                <input type="number" step="0.01" className={inputClass} value={form.installmentAmount} onChange={e => setForm({ ...form, installmentAmount: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Total Parcelas *</label>
                <input type="number" className={inputClass} value={form.totalInstallments} onChange={e => setForm({ ...form, totalInstallments: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                {/* Parcelas pagas é DERIVADA das despesas pagas — o backend nem
                    aceita mais o campo. O que se declara aqui é o histórico
                    anterior ao HUB, que o sistema não tem como descobrir. */}
                <label className={labelClass}>Parcelas pagas antes do HUB</label>
                <input type="number" min={0} className={inputClass} value={form.paidInstallmentsBeforeHub} onChange={e => setForm({ ...form, paidInstallmentsBeforeHub: e.target.value })} />
                <p className="text-[11px] text-slate-500 mt-1">
                  {contract
                    ? `Total pago hoje: ${contract.paidInstallments || 0}, calculado pelas despesas`
                    : 'Quitadas antes de o contrato entrar no sistema'}
                </p>
              </div>
              <div>
                <label className={labelClass}>Carência (meses)</label>
                <input type="number" className={inputClass} value={form.gracePeriodMonths} onChange={e => setForm({ ...form, gracePeriodMonths: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Taxa Mensal (%)</label>
                <input type="number" step="0.0001" className={inputClass} value={form.interestRateMonthly} onChange={e => setForm({ ...form, interestRateMonthly: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Taxa Anual (%)</label>
                <input type="number" step="0.0001" className={inputClass} value={form.interestRateAnnual} onChange={e => setForm({ ...form, interestRateAnnual: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Data Início *</label>
                <input type="date" className={inputClass} value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Data Fim</label>
                <input type="date" className={inputClass} value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} />
              </div>
            </div>
            {/* Saldo informado pelo credor.
                Vence a projeção `parcelas restantes x valor da parcela`, que erra
                por construção em contrato corrigido por índice: ela congela o
                valor da parcela enquanto SELIC/IPCA continuam subindo. */}
            <div className="p-3 bg-slate-700/30 rounded-lg space-y-3">
              <div>
                <p className="text-sm font-medium text-slate-300">Saldo informado pelo credor</p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Opcional. Preenchido, vira a âncora do saldo: o HUB parte dele e desconta
                  as parcelas pagas depois da data da consulta.
                </p>
                {/* Aviso caro de aprender: o Sicredi chama de "saldo devedor" duas
                    coisas diferentes. Movimentações do Título traz a SOMA DAS
                    PRESTAÇÕES (é o que o HUB usa); o Documento Descritivo de
                    Crédito traz o PRINCIPAL de quitação, que é bem menor porque não
                    inclui juros futuros. No Capital de Giro a diferença é R$ 2.630. */}
                <p className="text-[11px] text-amber-400/80 mt-1.5">
                  Só preencha se o saldo do credor for a <strong>soma das parcelas que faltam</strong>.
                  Confira multiplicando: parcelas restantes x valor da parcela tem que dar perto do
                  número informado.
                  <br />Sicredi PRICE (FAMPE, Capital de Giro): Crédito, Consultar,
                  Movimentações do Título, última linha. Bate exato.
                  <br />Sicredi <strong>SAC</strong>: não preencha. Ali o saldo é só o principal,
                  porque juros e correção entram mês a mês por fora.
                  <br />Não use o &quot;Documento Descritivo de Crédito&quot;: é valor de quitação.
                  <br />PGFN: Consulta de Negociações, Saldo Devedor <strong>com Juros</strong>.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Saldo devedor (R$)</label>
                  <input
                    type="number" step="0.01" min={0} className={inputClass}
                    value={form.sourceBalance}
                    onChange={e => setForm({ ...form, sourceBalance: e.target.value })}
                    placeholder="deixe vazio para projetar"
                  />
                </div>
                <div>
                  <label className={labelClass}>Data da consulta</label>
                  <input
                    type="date" className={inputClass}
                    value={form.sourceBalanceDate}
                    onChange={e => setForm({ ...form, sourceBalanceDate: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className={labelClass}>Despesa Recorrente Vinculada</label>
              <select className={ctx.baseErro ? `w-full ${campoSomenteLeitura} rounded-lg px-3 py-2 text-sm` : inputClass} disabled={!!ctx.baseErro} value={form.recurringExpenseId} onChange={e => setForm({ ...form, recurringExpenseId: e.target.value })}>
                <option value="">Nenhuma</option>
                {recurringExpenses.map((r: any) => <option key={r.id} value={r.id}>{r.description} (R$ {fmt(r.amount)})</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Observações</label>
              <textarea className={inputClass} rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </form>
      </CascaDeModal>
    );
  };

  const UploadModal = ({ contract, onClose , ctx }: { contract: any; onClose: () => void; ctx: LoanModalCtx }) => {
  const { showToast, schedule, loadContracts, loadSummary, handleDownloadContract, bankAccounts, suppliers, setUploadModal, recurringExpenses, handleSync, handleReprocessInvoices, handleCreateBorderoExpenses } = ctx;
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleUpload = async () => {
      if (!file) return;
      setUploading(true);
      try {
        await loanContractsService.uploadContract(contract.id, file);
        showToast('Contrato PDF enviado');
        await loadContracts();
        onClose();
      } catch (e) {
        showToast('Erro ao enviar arquivo', 'error');
      } finally {
        setUploading(false);
      }
    };

    return (
      // Arquivo escolhido: não fecha ao clicar fora (como já era).
      <CascaDeModal
        largura="xs"
        titulo="Upload Contrato PDF"
        subtitulo={`${contract.contractNumber} - ${contract.description}`}
        onClose={onClose}
        corpoClassName="p-0"
        rodape={
          <div className="ml-auto flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancelar</button>
            <button onClick={handleUpload} disabled={!file || uploading} className="px-6 py-2 bg-primary hover:bg-primary-strong text-white text-sm font-medium rounded-lg disabled:opacity-50">
              {uploading ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        }
      >
          <div
            className="p-6 space-y-4"
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              if (!uploading) setFile(e.dataTransfer?.files?.[0] || null);
            }}
          >
            {contract.attachmentS3Key && (
              <div className={`flex items-center gap-2 p-3 border rounded-lg ${TONE_SURFACE.success} ${TONE_BORDER.success}`}>
                <span className={`text-sm ${TONE_TEXT.success}`}>Contrato PDF já anexado</span>
                <button onClick={() => handleDownloadContract(contract)} className="text-slate-300 hover:text-white ml-auto">
                  <Icon name="download" className="w-4 h-4" />
                </button>
              </div>
            )}
            <input
              type="file"
              accept=".pdf"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-700 file:text-slate-300 file:text-sm file:font-medium hover:file:bg-slate-600"
            />
          </div>
      </CascaDeModal>
    );
  };

  const BorderoImportModal = ({ onClose , ctx }: { onClose: () => void; ctx: LoanModalCtx }) => {
  const { showToast, schedule, loadContracts, loadSummary, handleDownloadContract, bankAccounts, suppliers, setUploadModal, recurringExpenses, handleSync, handleReprocessInvoices, handleCreateBorderoExpenses } = ctx;
    const [file, setFile] = useState<File | null>(null);
    const [selectedBank, setSelectedBank] = useState('');
    const [importing, setImporting] = useState(false);

    const handleImport = async () => {
      if (!file || !selectedBank) return;
      setImporting(true);
      try {
        const result = await loanContractsService.importBordero(Number(selectedBank), file);
        showToast(`Borderô importado: ${result.contractNumber} - ${result.discountedReceivables?.length || 0} recebíveis`);
        await Promise.all([loadContracts(), loadSummary()]);
        onClose();
      } catch (e: any) {
        showToast(e.response?.data?.message || 'Erro ao importar borderô', 'error');
      } finally {
        setImporting(false);
      }
    };

    const inputClass = `w-full ${campo} rounded-lg px-3 py-2 text-sm`;
    const labelClass = 'block text-sm font-medium text-slate-300 mb-1';

    return (
      // Arquivo e conta escolhidos: não fecha ao clicar fora (como já era).
      <CascaDeModal
        largura="xs"
        titulo="Importar Borderô"
        onClose={onClose}
        corpoClassName="p-0"
        rodape={
          <div className="ml-auto flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancelar</button>
            <button
              onClick={handleImport}
              disabled={!file || !selectedBank || importing}
              className="px-6 py-2 bg-primary hover:bg-primary-strong text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {importing ? 'Importando...' : 'Importar Borderô'}
            </button>
          </div>
        }
      >
          <div
            className="p-6 space-y-4"
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              if (!importing) setFile(e.dataTransfer?.files?.[0] || null);
            }}
          >
            <p className="text-sm text-slate-400">
              Faça upload do borderô de desconto (PDF do Sicredi). O contrato será criado automaticamente com os dados extraídos.
            </p>
            <div>
              <label className={labelClass}>Conta Bancária *</label>
              <select className={inputClass} value={selectedBank} onChange={e => setSelectedBank(e.target.value)}>
                <option value="">Selecione...</option>
                {bankAccounts.map((b: any) => <option key={b.id} value={b.id}>{b.bankName} - {b.accountNumber}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Borderô PDF *</label>
              <input
                type="file"
                accept=".pdf"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-700 file:text-slate-300 file:text-sm file:font-medium hover:file:bg-slate-600"
              />
            </div>
          </div>
      </CascaDeModal>
    );
  };

  const DetailModal = ({ contract, onClose , ctx }: { contract: any; onClose: () => void; ctx: LoanModalCtx }) => {
  const { showToast, schedule, loadContracts, loadSummary, handleDownloadContract, bankAccounts, suppliers, setUploadModal, recurringExpenses, handleSync, handleReprocessInvoices, handleCreateBorderoExpenses } = ctx;
    const isDesconto = contract.type === 'desconto_duplicatas';
    // Vem do backend, não recalcula aqui. O recálculo local ignorava a carência
    // (o backend faz `total − carência − pagas`) e usava o `installmentAmount`
    // nominal, que congela em contrato corrigido por índice. O mesmo contrato
    // mostrava um valor no card e outro neste modal.
    const summaryContract = ctx.summary?.contracts?.find((sc: any) => sc.id === contract.id);
    const progress = Number(summaryContract?.progress) || 0;
    const pagasReais = Number(summaryContract?.paidInstallments ?? contract.paidInstallments) || 0;
    const prestacoesReais = Number(summaryContract?.payableInstallments) || Number(contract.totalInstallments) || 0;
    const remaining = isDesconto ? 0 : Number(summaryContract?.remaining ?? 0);

    // For desconto: calculate totals from invoices paid by clients
    const invoiceItems = isDesconto ? schedule.filter((s: any) => s.txType === 'invoice') : [];
    const paidInvoiceItems = invoiceItems.filter((s: any) => s.status === 'paga');
    const descontoTotalLiquidado = paidInvoiceItems.reduce((sum: number, s: any) => sum + Number(s.amount), 0);
    const descontoFaceValue = Number(contract.faceValue) || Number(contract.originalAmount);
    const descontoRemaining = Math.max(0, descontoFaceValue - descontoTotalLiquidado);
    const descontoProgress = descontoFaceValue > 0 ? Math.round((descontoTotalLiquidado / descontoFaceValue) * 100) : 0;

    return (
      // Ficha de leitura com ações: não fecha ao clicar fora (como já era). Uma
      // primária por variante (§8.1): Sincronizar no empréstimo, Criar Despesas no
      // desconto de duplicatas. O resto neutro.
      <CascaDeModal
        largura="md"
        titulo={contract.contractNumber}
        subtitulo={contract.description}
        selos={isDesconto ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TONE_BADGE.info}`}>Desc. Duplicatas</span> : undefined}
        onClose={onClose}
        corpoClassName="p-6 space-y-6"
        rodape={
          <>
            <div className="flex gap-2 flex-wrap">
              {!isDesconto && (
                <button onClick={() => { onClose(); handleSync(contract.id); }} className="px-4 py-2 text-sm bg-primary text-white hover:bg-primary-strong rounded-lg flex items-center gap-2">
                  <Icon name="refresh" className="w-4 h-4" /> Sincronizar Parcelas
                </button>
              )}
              {isDesconto && (
                <button onClick={() => { onClose(); handleReprocessInvoices(contract.id); }} className="px-4 py-2 text-sm bg-slate-700 text-slate-300 hover:bg-slate-600 rounded-lg flex items-center gap-2">
                  <Icon name="refresh" className="w-4 h-4" /> Reprocessar Faturas
                </button>
              )}
              {isDesconto && (
                <button onClick={() => { onClose(); handleCreateBorderoExpenses(contract.id); }} className="px-4 py-2 text-sm bg-primary text-white hover:bg-primary-strong rounded-lg flex items-center gap-2">
                  <Icon name="plus" className="w-4 h-4" /> Criar Despesas
                </button>
              )}
              <button onClick={() => { onClose(); setUploadModal(contract); }} className="px-4 py-2 text-sm bg-slate-700 text-slate-300 hover:bg-slate-600 rounded-lg flex items-center gap-2">
                <Icon name="upload" className="w-4 h-4" /> Upload {isDesconto ? 'Borderô' : 'PDF'}
              </button>
              {contract.attachmentS3Key && (
                <button onClick={() => handleDownloadContract(contract)} className="px-4 py-2 text-sm bg-slate-700 text-slate-300 hover:bg-slate-600 rounded-lg flex items-center gap-2">
                  <Icon name="download" className="w-4 h-4" /> Ver {isDesconto ? 'Borderô' : 'Contrato'}
                </button>
              )}
            </div>
            <button onClick={onClose} className="ml-auto px-4 py-2 text-sm text-slate-400 hover:text-white">Fechar</button>
          </>
        }
      >
            {/* Info cards — different for desconto */}
            {isDesconto ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Valor Títulos (Face)</p>
                  <p className="text-lg font-bold text-white">R$ {fmt(contract.faceValue || contract.originalAmount)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Valor Líquido Creditado</p>
                  <p className="text-lg font-bold text-white">R$ {fmt(contract.netAmountReceived)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Deságio (não-caixa)</p>
                  <p className="text-lg font-bold text-amber-400">R$ {fmt(contract.encargosAmount ?? (Number(contract.faceValue || contract.originalAmount) - Number(contract.netAmountReceived || 0)))}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">já abatido na liberação</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">IOF Total</p>
                  <p className="text-lg font-bold text-red-400">R$ {fmt(contract.iofAmount)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Tarifa Bancária</p>
                  <p className="text-lg font-bold text-red-400">R$ {fmt(contract.tarifaAmount)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Custo Total do Desconto</p>
                  <p className={`text-lg font-bold ${TONE_TEXT.danger}`}>R$ {fmt(Number(contract.iofAmount || 0) + Number(contract.tarifaAmount || 0) + Number(contract.encargosAmount ?? (Number(contract.faceValue || contract.originalAmount) - Number(contract.netAmountReceived || 0))))}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">deságio + IOF + tarifa</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Valor Original</p>
                  <p className="text-lg font-bold text-white">R$ {fmt(contract.originalAmount)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Parcela</p>
                  <p className="text-lg font-bold text-white">R$ {fmt(contract.installmentAmount)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Restante</p>
                  <p className={`text-lg font-bold ${TONE_TEXT.warning}`}>R$ {fmt(remaining)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400">Taxa Mensal</p>
                  <p className="text-lg font-bold text-white">{contract.interestRateMonthly ? `${parseFloat(contract.interestRateMonthly).toFixed(2)}%` : '—'}</p>
                </div>
              </div>
            )}

            {/* Progress */}
            {isDesconto ? (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">R$ {fmt(descontoTotalLiquidado)} de R$ {fmt(descontoFaceValue)} liquidado</span>
                  <span className="text-slate-200 font-medium">{descontoProgress}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <div className="bg-gradient-to-r from-purple-500 to-purple-400 h-3 rounded-full transition-all" style={{ width: `${Math.min(descontoProgress, 100)}%` }} />
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">
                    {pagasReais} de {prestacoesReais} prestações pagas
                    {summaryContract?.inGrace && `, em carência até ${fmtDate(summaryContract.graceUntil)}`}
                  </span>
                  <span className="text-slate-200 font-medium">{progress}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <div className="bg-primary h-3 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            {/* Recebíveis Descontados (borderô only) */}
            {isDesconto && contract.discountedReceivables?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">
                  Recebíveis Descontados ({contract.discountedReceivables.length})
                </h3>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-central-borda">
                  <table className="w-full text-sm">
                    <CabecalhoInterno fixoNaCaixa>
                      <tr>
                        <th className="px-3 py-2 text-xs text-slate-400 uppercase text-left">Nosso N°</th>
                        <th className="px-3 py-2 text-xs text-slate-400 uppercase text-left">Sacado</th>
                        <th className="px-3 py-2 text-xs text-slate-400 uppercase text-center">Vencimento</th>
                        <th className="px-3 py-2 text-xs text-slate-400 uppercase text-right">Valor</th>
                      </tr>
                    </CabecalhoInterno>
                    <tbody className="divide-y divide-slate-700/50">
                      {contract.discountedReceivables.map((r: any, i: number) => (
                        <tr key={i} className="hover:bg-slate-700/30 transition-colors">
                          <td className="px-3 py-2 text-slate-300 font-mono text-xs">{r.nossoNumero}</td>
                          <td className="px-3 py-2 text-slate-300 text-xs truncate max-w-[200px]">{r.sacado}</td>
                          <td className="px-3 py-2 text-slate-400 text-center text-xs">{r.vencimento}</td>
                          <td className="px-3 py-2 text-white text-right font-medium">R$ {fmt(r.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-700/30">
                      <tr>
                        <td colSpan={3} className="px-3 py-2 text-xs text-slate-400 font-medium text-right">Total</td>
                        <td className="px-3 py-2 text-white text-right font-bold">R$ {fmt(contract.discountedReceivables.reduce((s: number, r: any) => s + (Number(r.valor) || 0), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Info row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-400">{isDesconto ? 'Data Operação' : 'Início'}</p>
                <p className="text-white">{fmtDate(contract.startDate)}</p>
              </div>
              <div>
                <p className="text-slate-400">Fim</p>
                <p className="text-white">{fmtDate(contract.endDate)}</p>
              </div>
              <div>
                <p className="text-slate-400">Instituição</p>
                <p className="text-white">{contract.supplier?.name || '—'}</p>
              </div>
              <div>
                <p className="text-slate-400">Conta</p>
                <p className="text-white">{contract.bankAccount?.bankName || '—'}</p>
              </div>
            </div>

            {contract.notes && (
              <div className="p-3 bg-slate-700/30 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">Observações</p>
                <p className="text-sm text-slate-300">{contract.notes}</p>
              </div>
            )}

            {/* Payment schedule / Transactions */}
            {/* Faturas Antecipadas (borderô only) */}
            {isDesconto && invoiceItems.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">
                  Faturas Antecipadas ({paidInvoiceItems.length}/{invoiceItems.length} pagas)
                </h3>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-central-borda">
                  <table className="w-full text-sm">
                    <CabecalhoInterno fixoNaCaixa>
                      <tr>
                        <th className="px-4 py-2 text-xs text-slate-400 uppercase text-left">Fatura</th>
                        <th className="px-4 py-2 text-xs text-slate-400 uppercase text-left">Vencimento</th>
                        <th className="px-4 py-2 text-xs text-slate-400 uppercase text-left">Pagamento</th>
                        <th className="px-4 py-2 text-xs text-slate-400 uppercase text-right">Valor</th>
                        <th className="px-4 py-2 text-xs text-slate-400 uppercase text-center">Status</th>
                      </tr>
                    </CabecalhoInterno>
                    <tbody className="divide-y divide-slate-700/50">
                      {invoiceItems.map((inv: any, i: number) => {
                        // Determine badge based on schedule status + discountStatus.
                        // 'liquidado_apos_estorno' wins precedence to flag that this
                        // title was estornado but compensated by direct payment.
                        let badgeClass = 'bg-yellow-500/20 text-yellow-400';
                        let badgeText = 'Pendente';
                        // Estornado mas já pago pelo sacado = resolvido (liq. após estorno).
                        // Vermelho "Estornado" só pro estornado AINDA não quitado.
                        if (inv.discountStatus === 'liquidado_apos_estorno' || (inv.discountStatus === 'estornado' && inv.status === 'paga')) {
                          badgeClass = 'bg-amber-500/20 text-amber-400';
                          badgeText = 'Liq. após estorno';
                        } else if (inv.discountStatus === 'estornado') {
                          badgeClass = 'bg-red-500/20 text-red-400';
                          badgeText = 'Estornado';
                        } else if (inv.status === 'paga') {
                          badgeClass = TONE_BADGE.neutral;
                          badgeText = 'Paga';
                        }
                        return (
                          <tr key={inv.invoiceId || i} className="hover:bg-slate-700/30 transition-colors">
                            <td className="px-4 py-2 text-slate-300 text-xs">{inv.description}</td>
                            <td className="px-4 py-2 text-slate-300 whitespace-nowrap">{fmtDate(inv.dueDate)}</td>
                            <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{inv.paymentDate ? fmtDate(inv.paymentDate) : '—'}</td>
                            <td className="px-4 py-2 text-white text-right font-medium">R$ {fmt(inv.amount)}</td>
                            <td className="px-4 py-2 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                                {badgeText}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-700/30">
                      <tr>
                        <td colSpan={3} className="px-4 py-2 text-xs text-slate-400 font-medium text-right">Pago / Total</td>
                        <td className="px-4 py-2 text-right font-bold text-white">R$ {fmt(descontoTotalLiquidado)} / R$ {fmt(descontoFaceValue)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {ctx.scheduleErro && (
              <p role="alert" className={`text-sm ${TONE_TEXT.warning}`} data-testid="cronograma-que-nao-carregou">Não consegui carregar {isDesconto ? 'as transações do borderô' : 'o cronograma de parcelas'}: {ctx.scheduleErro}.</p>
            )}
            {/* Transações Bancárias (borderô) or Cronograma de Parcelas (empréstimo) */}
            {(isDesconto ? schedule.filter((s: any) => s.txType !== 'invoice').length > 0 : schedule.length > 0) && (
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">
                  {isDesconto ? 'Transações Bancárias' : 'Cronograma de Parcelas'}
                </h3>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-central-borda">
                  <table className="w-full text-sm">
                    <CabecalhoInterno fixoNaCaixa>
                      {isDesconto ? (
                        <tr>
                          <th className="px-4 py-2 text-xs text-slate-400 uppercase text-left">Data</th>
                          <th className="px-4 py-2 text-xs text-slate-400 uppercase text-left">Descrição</th>
                          <th className="px-4 py-2 text-xs text-slate-400 uppercase text-center">Tipo</th>
                          <th className="px-4 py-2 text-xs text-slate-400 uppercase text-right">Valor</th>
                        </tr>
                      ) : (
                        <tr>
                          <th className="px-4 py-2 text-xs text-slate-400 uppercase text-center">#</th>
                          <th className="px-4 py-2 text-xs text-slate-400 uppercase text-left">Vencimento</th>
                          <th className="px-4 py-2 text-xs text-slate-400 uppercase text-left">Pagamento</th>
                          <th className="px-4 py-2 text-xs text-slate-400 uppercase text-right">Valor</th>
                          <th className="px-4 py-2 text-xs text-slate-400 uppercase text-center">Status</th>
                        </tr>
                      )}
                    </CabecalhoInterno>
                    <tbody className="divide-y divide-slate-700/50">
                      {isDesconto ? schedule.filter((s: any) => s.txType !== 'invoice').map((tx: any, i: number) => (
                        <tr key={tx.transactionId || i} className="hover:bg-slate-700/30 transition-colors">
                          <td className="px-4 py-2 text-slate-300 whitespace-nowrap">{fmtDate(tx.dueDate)}</td>
                          <td className="px-4 py-2 text-slate-300 text-xs">{tx.description}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tx.txType === 'credit' ? TONE_BADGE.neutral : TONE_BADGE.danger}`}>
                              {tx.txType === 'credit' ? 'Crédito' : 'Débito'}
                            </span>
                          </td>
                          <td className={`px-4 py-2 text-right font-medium ${tx.txType === 'credit' ? 'text-central-texto' : 'text-red-400'}`}>
                            {tx.txType === 'credit' ? '+' : '−'} R$ {fmt(tx.amount)}
                          </td>
                        </tr>
                      )) : schedule.map((inst: any) => {
                        const sLabel = inst.status === 'paga' ? 'Paga' : inst.status === 'vencida' ? 'Vencida' : inst.status === 'carencia' ? 'Carência' : 'Pendente';
                        return (
                          <tr key={inst.number} className="hover:bg-slate-700/30 transition-colors">
                            <td className="px-4 py-2 text-slate-400 text-center font-mono">{inst.number}/{contract.totalInstallments}</td>
                            <td className="px-4 py-2 text-slate-300">{fmtDate(inst.dueDate)}</td>
                            <td className="px-4 py-2 text-slate-400">{inst.paymentDate ? fmtDate(inst.paymentDate) : '—'}</td>
                            <td className="px-4 py-2 text-white text-right font-medium">{inst.status === 'carencia' ? '—' : `R$ ${fmt(inst.amount)}`}</td>
                            <td className="px-4 py-2 text-center">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${inst.status === 'paga' ? TONE_BADGE.open : inst.status === 'vencida' ? TONE_BADGE.danger : inst.status === 'carencia' ? TONE_BADGE.neutral : TONE_BADGE.warning}`}>{sLabel}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

      </CascaDeModal>
    );
  };

/**
 * `onOpenExpenses` leva o número do contrato para a tela de Despesas com a busca
 * já aplicada. Usa o mesmo mecanismo que o extrato bancário já usa
 * (`App.tsx` → `expenseSearch` → `ExpensesTab.initialSearch`), então não exige
 * prop nova no ExpensesTab nem endpoint novo.
 */
export default function LoanContractsTab({ onOpenExpenses }: { onOpenExpenses?: (contractNumber: string) => void } = {}) {
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [recurringExpenses, setRecurringExpenses] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);

  // Modals
  const [formModal, setFormModal] = useState<'create' | 'edit' | null>(null);
  const [editingContract, setEditingContract] = useState<any>(null);
  const [detailModal, setDetailModal] = useState<any>(null);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [scheduleErro, setScheduleErro] = useState<string | null>(null);
  // D5 (11/10/2026): as três cargas calavam. Lista vazia virava "Nenhum contrato cadastrado" com o "Novo" ao lado (o
  // create não tem dedup); o resumo que falhava sumia com os cards e a faixa de alertas inteira (tela limpa = "em dia").
  const cargaContratos = useEstadoDaCarga();
  const cargaResumo = useEstadoDaCarga();
  const cargaBase = useEstadoDaCarga();
  const [uploadModal, setUploadModal] = useState<any>(null);
  const confirmar = useConfirm();
  const [borderoModal, setBorderoModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'emprestimos' | 'borderos'>('emprestimos');


  // D6: o toast próprio da tela vira o aviso do HUB (o nome fica, para não mexer em cada chamada).
  const avisar = useAviso();
  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => avisar(message, type);

  const rodarContratos = cargaContratos.rodar;
  const rodarResumo = cargaResumo.rodar;
  const rodarBase = cargaBase.rodar;
  const loadContracts = useCallback(async () => {
    await rodarContratos(async () => {
      const data = await loanContractsService.getAll();
      setContracts(Array.isArray(data) ? data : []);
    });
  }, [rodarContratos]);

  const loadSummary = useCallback(async () => {
    await rodarResumo(async () => {
      const data = await loanContractsService.getSummary();
      setSummary(data);
    });
  }, [rodarResumo]);

  const loadBase = useCallback(async () => {
    await rodarBase(async () => {
      const [supps, banks, recs] = await Promise.all([
        suppliersService.getAll(),
        bankAccountsService.getAll(),
        recurringExpensesService.getAll(),
      ]);
      setSuppliers(Array.isArray(supps) ? supps : supps.data || []);
      setBankAccounts(Array.isArray(banks) ? banks : []);
      setRecurringExpenses(Array.isArray(recs) ? recs : []);
    });
  }, [rodarBase]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadContracts(), loadSummary(), loadBase()]);
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Era um CascaDeModal próprio com estado confirmDelete; agora o useConfirm.
  const setConfirmDelete = async (c: any) => {
    if (!c) return;
    const recorrente = c.recurringExpenseId
      ? (recurringExpenses.find((r: any) => r.id === Number(c.recurringExpenseId)) || { id: Number(c.recurringExpenseId) })
      : null;
    if (!(await confirmar(confirmacaoDeExcluirContrato(c, recorrente)))) return;
    const excluiu = await handleDelete(c.id);
    if (excluiu && recorrente && (recorrente as any).isActive !== false && (await confirmar(confirmacaoDeDesativarRecorrente(recorrente)))) {
      try {
        await recurringExpensesService.update(recorrente.id, { isActive: false });
        showToast('Despesa recorrente desativada');
        await loadBase();
      } catch (e: any) {
        showToast(`Não desativei a despesa recorrente: ${e?.response?.data?.message || mensagemDoErroDeCarga(e)}`, 'error');
      }
    }
  };

  const handleDelete = async (id: number): Promise<boolean> => {
    try {
      await loanContractsService.delete(id);
      showToast('Contrato excluído');
      await Promise.all([loadContracts(), loadSummary()]);
      return true;
    } catch (e: any) {
      showToast(`Não excluí o contrato: ${e?.response?.data?.message || mensagemDoErroDeCarga(e)}`, 'error');
      return false;
    }
  };

  const handleSync = async (id: number) => {
    try {
      await loanContractsService.syncInstallments(id);
      showToast('Parcelas sincronizadas');
      await Promise.all([loadContracts(), loadSummary()]);
    } catch (e: any) {
      showToast(e.response?.data?.message || 'Erro ao sincronizar', 'error');
    }
  };

  const handleReprocessInvoices = async (id: number) => {
    try {
      const result = await loanContractsService.reprocessInvoices(id);
      showToast(`${result.marked || 0} de ${result.total || 0} faturas marcadas como antecipadas`);
      await Promise.all([loadContracts(), loadSummary()]);
    } catch (e: any) {
      showToast(e.response?.data?.message || 'Erro ao reprocessar faturas', 'error');
    }
  };

  const handleCreateBorderoExpenses = async (id: number) => {
    const c = contracts.find((x: any) => x.id === id);
    if (!(await confirmar(confirmacaoDasDespesasDoBordero(c || { contractNumber: `#${id}` })))) return;
    try {
      const result = await loanContractsService.createBorderoExpenses(id);
      // D3: o servidor diz se criou ou reaproveitou (`alreadyExisted`, backend #424); o aviso segue o que ele disse.
      const aviso = avisoDasDespesasDoBordero(result || {});
      showToast(aviso.mensagem, aviso.tom);
      await Promise.all([loadContracts(), loadSummary()]);
    } catch (e: any) {
      showToast(e.response?.data?.message || 'Erro ao criar despesas do borderô', 'error');
    }
  };

  const openDetail = async (contract: any) => {
    // O cronograma do contrato anterior não pode aparecer sob este enquanto a leitura não volta.
    setSchedule([]);
    setScheduleErro(null);
    setDetailModal(contract);
    try {
      const data = await loanContractsService.getSchedule(contract.id);
      setSchedule(data.installments || []);
    } catch (e) { setSchedule([]); setScheduleErro(mensagemDoErroDeCarga(e)); }
  };

  const handleDownloadContract = async (contract: any) => {
    try {
      const data = await loanContractsService.getContractUrl(contract.id);
      window.open(data.url, '_blank');
    } catch (e) {
      showToast('Nenhum contrato PDF anexado', 'error');
    }
  };

  // ============ CONTRACT FORM ============

  // ============ UPLOAD MODAL ============

  // ============ BORDERO IMPORT MODAL ============

  // ============ DETAIL MODAL (SCHEDULE) ============

  const resumoDe = (id: number) => summary?.contracts?.find((sc: any) => sc.id === id);
  // A PARCELA VENCIDA (12/10/2026): "venceu e não consta débito" passa a ser decidido pela despesa do mês e pelo alcance
  // do extrato da conta, não só por `paidInstallments` (que só anda no Sincronizar). A leitura carrega a chave das parcelas
  // que pediu; resposta de uma chave que já não está na tela é descartada.
  const vencidas = contracts.filter((c: any) => c.type !== 'desconto_duplicatas')
    .filter((c: any) => c.status === 'ativo')
    .map((c: any) => ({ c, prox: calcProximaParcela(c, resumoDe(c.id)) }))
    .filter((x: any) => resumoDe(x.c.id) && x.prox?.estado === 'vencida');
  const chaveDasVencidas = vencidas.map((x: any) => `${x.c.id}|${x.prox.dueDate}`).join(',');
  const [leituraDasParcelas, setLeituraDasParcelas] = useState<{ chave: string; por: Record<number, LeituraDaParcela> } | null>(null);
  const chavePedidaRef = useRef('');
  useEffect(() => {
    chavePedidaRef.current = chaveDasVencidas;
    if (!chaveDasVencidas) return;
    const pedido = chaveDasVencidas;
    const itens = vencidas.map((x: any) => ({ id: x.c.id, conta: Number(x.c.bankAccountId) || null, venc: String(x.prox.dueDate) }));
    (async () => {
      const meses = Array.from(new Set(itens.map((x) => x.venc.slice(0, 7))));
      const despesas: any[] | null = await expensesService.getAll({ month: meses.join(',') })
        .then((r: any) => (Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []), () => null);
      const contas = Array.from(new Set(itens.map((x) => x.conta).filter((x): x is number => !!x)));
      const extrato: Record<number, string | null | undefined> = {};
      await Promise.all(contas.map(async (conta) => {
        try {
          const r: any = await bankAccountsService.getTransactions(conta, { page: 1, limit: 1 });
          const linha = Array.isArray(r?.data) ? r.data[0] : Array.isArray(r) ? r[0] : null;
          extrato[conta] = linha?.date ? String(linha.date).slice(0, 10) : null;
        } catch { extrato[conta] = undefined; }
      }));
      if (chavePedidaRef.current !== pedido) return;
      const por: Record<number, LeituraDaParcela> = {};
      for (const x of itens) por[x.id] = lerParcelaVencida(x.id, x.venc, despesas, x.conta ? extrato[x.conta] : null);
      setLeituraDasParcelas({ chave: pedido, por });
    })();
  }, [chaveDasVencidas]); // eslint-disable-line react-hooks/exhaustive-deps
  const leituraDa = (id: number): LeituraDaParcela | 'lendo' =>
    leituraDasParcelas && leituraDasParcelas.chave === chaveDasVencidas ? (leituraDasParcelas.por[id] || 'lendo') : 'lendo';

  // ============ LOADING ============
  if (loading && cargaContratos.atualizadoEm === null && cargaContratos.estado !== 'erro') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  const emprestimos = contracts.filter(c => c.type !== 'desconto_duplicatas');
  const borderos = contracts.filter(c => c.type === 'desconto_duplicatas');
  const filteredContracts = activeTab === 'emprestimos' ? emprestimos : borderos;

  const resumoDoContrato = (id: number) => summary?.contracts?.find((sc: any) => sc.id === id);

  // "Pendente Liquidação" só soma borderô que TEM títulos importados.
  //
  // Filtrar por `status === 'ativo'` seria frágil: o campo não é escrito por
  // caminho nenhum em borderô, então hoje ele diz o inverso da verdade (os 4
  // liquidados constam "ativo" e o único "quitado" é o que não tem título). Dava
  // R$ 0,00 por coincidência, e voltaria a errar no dia em que alguém corrigisse
  // os status. O que separa de verdade é ter ou não a lista de duplicatas: sem
  // ela o sistema não sabe se liquidou, e somar como pendente é chutar — foi o
  // que colocou os R$ 34.679,62 do C639301270 no card.
  //
  // "Total Antecipado" continua somando todos: ele responde quanto de antecipação
  // foi usado, e isso inclui o que já se resolveu.
  const borderoSummaryData = (() => {
    if (!summary?.contracts) return { totalFace: 0, totalLiquido: 0, pendente: 0, liquidados: 0, semTitulos: 0 };
    const bc = borderos.map((c: any) => ({ contrato: c, resumo: resumoDoContrato(c.id) }));
    const comTitulos = bc.filter((b: any) => estadoBordero(b.contrato, b.resumo).chave !== 'sem_titulos');
    return {
      totalFace: bc.reduce((s: number, b: any) => s + (Number(b.resumo?.faceValue ?? b.contrato.faceValue) || 0), 0),
      totalLiquido: bc.reduce((s: number, b: any) => s + (Number(b.contrato.netAmountReceived) || 0), 0),
      pendente: comTitulos.reduce((s: number, b: any) => s + (Number(b.resumo?.remaining) || 0), 0),
      liquidados: bc.filter((b: any) => estadoBordero(b.contrato, b.resumo).chave === 'liquidado').length,
      semTitulos: bc.length - comTitulos.length,
    };
  })();

  // Os totais de empréstimo vêm PRONTOS do backend, que já filtra por `ativo` e
  // já exclui de `monthlyPayment` quem está em carência. Recalcular no front
  // somando `remaining` de todo mundo (era o que acontecia) reintroduz aqui o
  // mesmo defeito do card de borderô no dia em que um empréstimo for quitado.
  const emprestimoSummaryData = {
    totalDebt: Number(summary?.totalDebt) || 0,
    monthlyPayment: Number(summary?.monthlyPayment) || 0,
    ativos: (summary?.contracts || []).filter(
      (sc: any) => sc.type !== 'desconto_duplicatas' && sc.status === 'ativo',
    ).length,
  };

  const emCarencia = (summary?.contracts || []).filter(
    (sc: any) => sc.type !== 'desconto_duplicatas' && sc.status === 'ativo' && sc.inGrace,
  );

  // Faixa de alerta: o uso "só abro quando desconfio" vira a tela apontando
  // sozinha o que está estranho, em vez de esperar alguém procurar.
  const alertas = (() => {
    const out: Array<string | { texto: string; sincronizar?: number }> = [];
    for (const c of emprestimos) {
      const r = resumoDoContrato(c.id);
      if (!r || c.status !== 'ativo') continue;
      const prox = calcProximaParcela(c, r);
      if (prox?.estado === 'vencida') {
        const l = leituraDa(c.id);
        if (l === 'lendo') out.push(`${c.contractNumber}: parcela ${prox.numero}/${prox.total} de ${fmtDate(prox.dueDate)}: conferindo a despesa e o extrato…`);
        else out.push({ texto: fraseDaParcelaVencida(c.contractNumber, prox, l), sincronizar: l.estado === 'paga' ? c.id : undefined });
      }
      // Saldo informado pelo credor envelhece em silêncio: ele NÃO acompanha as
      // parcelas que forem pagas depois da consulta, então continua exibindo o
      // valor do dia em que foi digitado. Sem este aviso, o número fica velho e
      // parece atual — que é a forma como o card da dívida mentiu por dois anos.
      const dias = r.balanceSource === 'fonte' && r.sourceBalanceDate
        ? getDaysUntil(r.sourceBalanceDate) : null;
      if (dias !== null && dias <= -40) {
        out.push(`${c.contractNumber}: saldo informado pelo credor é de ${fmtDate(r.sourceBalanceDate)}, ${Math.abs(dias)} dias atrás — consulte de novo e atualize`);
      }
    }
    for (const c of borderos) {
      const r = resumoDoContrato(c.id);
      const est = estadoBordero(c, r);
      if (est.chave === 'aberto') {
        out.push(`${c.contractNumber}: R$ ${fmt(r?.remaining)} em títulos ainda não liquidados pelos sacados`);
      }
      if (est.chave === 'sem_titulos') {
        out.push(`${c.contractNumber}: borderô de ${fmtDate(c.startDate)} sem a lista de duplicatas — suba o PDF para saber se os sacados pagaram`);
      }
    }
    return out;
  })();

  const modalCtx: LoanModalCtx = { showToast, schedule, summary, loadContracts, loadSummary, handleDownloadContract, bankAccounts, suppliers, setUploadModal, recurringExpenses, handleSync, handleReprocessInvoices, handleCreateBorderoExpenses, baseErro: cargaBase.estado === 'erro' && cargaBase.atualizadoEm === null ? cargaBase.erro : null, scheduleErro };
  const listaPronta = cargaContratos.estado === 'pronto';
  const resumoNaoLido = cargaResumo.estado === 'erro' && cargaResumo.atualizadoEm === null;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Operações de Crédito</h1>
          <p className="text-sm text-slate-400 mt-1">Empréstimos, financiamentos e desconto de duplicatas</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'borderos' && listaPronta && (
            <button
              onClick={() => setBorderoModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary-strong text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Icon name="upload" className="w-4 h-4" /> Importar Borderô
            </button>
          )}
          {activeTab === 'emprestimos' && listaPronta && (
            <button
              onClick={() => { setEditingContract(null); setFormModal('create'); }}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary-strong text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Icon name="plus" className="w-4 h-4" /> Novo Contrato
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800 border border-slate-700 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('emprestimos')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'emprestimos'
              ? 'bg-orange-500/5 ring-1 ring-orange-500/60 text-orange-400'
              : 'text-slate-400 hover:text-slate-300 hover:bg-slate-700/50'
          }`}
        >
          Empréstimos
          {emprestimos.length > 0 && (
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${activeTab === 'emprestimos' ? 'bg-orange-500/20 text-orange-300' : 'bg-slate-700 text-slate-500'}`}>
              {emprestimos.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('borderos')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'borderos'
              ? 'bg-orange-500/5 ring-1 ring-orange-500/60 text-orange-400'
              : 'text-slate-400 hover:text-slate-300 hover:bg-slate-700/50'
          }`}
        >
          Borderôs
          {borderos.length > 0 && (
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${activeTab === 'borderos' ? 'bg-orange-500/20 text-orange-300' : 'bg-slate-700 text-slate-500'}`}>
              {borderos.length}
            </span>
          )}
        </button>
      </div>

      {/* D5: o resumo que não carrega diz; sem ele, a faixa de alertas não foi verificada (não é "tudo em dia"). */}
      {resumoNaoLido && (
        <div role="alert" className={`bg-slate-800/50 border border-amber-600/60 rounded-xl p-4 text-sm ${TONE_TEXT.warning}`} data-testid="resumo-que-nao-carregou">
          Não consegui ler o resumo ({cargaResumo.erro}): a dívida, a prestação mensal e os alertas (parcela vencida, saldo do credor velho, títulos em aberto) não foram verificados.
          <button onClick={loadSummary} className="ml-2 underline">Tentar de novo</button>
        </div>
      )}
      {/* Faixa de alerta — só aparece quando há algo a apontar */}
      {alertas.length > 0 && (
        <div className="bg-slate-800/50 border border-amber-600/60 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-amber-300 mb-1">
                {alertas.length === 1 ? 'Um ponto para conferir' : `${alertas.length} pontos para conferir`}
              </p>
              <ul className="space-y-0.5">
                {alertas.map((a, i) => (
                  <li key={i} className="text-sm text-amber-200/80">
                    {typeof a === 'string' ? a : a.texto}
                    {typeof a !== 'string' && a.sincronizar !== undefined && (
                      <button type="button" onClick={() => handleSync(a.sincronizar!)} className="ml-2 px-2 py-0.5 rounded bg-slate-700 text-slate-200 hover:bg-slate-600 text-xs">
                        Sincronizar parcelas
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Summary cards — Empréstimos */}
      {activeTab === 'emprestimos' && (summary || resumoNaoLido) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Cartões do topo (padrão das listas no Financeiro): o CartaoDoTopo. Não filtram; o número é texto. */}
          <CartaoDoTopo
            rotulo="Dívida Total Restante"
            valor={<span className={summary ? '' : 'text-slate-500'} data-kpi="divida">{summary ? `R$ ${fmt(emprestimoSummaryData.totalDebt)}` : '—'}</span>}
            sub="saldo devedor dos contratos ativos"
          />
          <CartaoDoTopo
            rotulo="Pagamento Mensal"
            valor={summary ? `R$ ${fmt(emprestimoSummaryData.monthlyPayment)}` : '—'}
            sub={emCarencia.length > 0 ? `${emCarencia.length} em carência, fora desta soma` : 'o que debita em conta por mês'}
          />
          <CartaoDoTopo
            rotulo="Contratos Ativos"
            valor={summary ? emprestimoSummaryData.ativos : '—'}
            sub={emCarencia.length > 0 ? `${emCarencia.length} ainda em carência` : 'todos em pagamento'}
          />
        </div>
      )}

      {/* Summary cards — Borderôs */}
      {activeTab === 'borderos' && (summary || resumoNaoLido) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* E1: os números são texto (o roxo, o verde e o amarelo saíram); o pendente pede atenção pela barra. */}
          <CartaoDoTopo
            rotulo="Total Antecipado (Face)"
            valor={summary ? `R$ ${fmt(borderoSummaryData.totalFace)}` : '—'}
            sub={`${borderos.length} operações, todo o histórico`}
          />
          <CartaoDoTopo
            rotulo="Valor Líquido Creditado"
            valor={summary ? `R$ ${fmt(borderoSummaryData.totalLiquido)}` : '—'}
            sub="o que entrou na conta, já sem deságio"
          />
          <CartaoDoTopo
            rotulo="Pendente Liquidação"
            valor={summary ? `R$ ${fmt(borderoSummaryData.pendente)}` : '—'}
            sub={borderoSummaryData.semTitulos > 0
              ? `${borderoSummaryData.semTitulos} sem títulos importados, fora desta conta`
              : 'títulos que o sacado ainda não pagou'}
            barra={summary && Number(borderoSummaryData.pendente) > 0 ? 'warning' : null}
          />
          <CartaoDoTopo
            rotulo="Borderôs Liquidados"
            valor={summary ? `${borderoSummaryData.liquidados}/${borderos.length}` : '—'}
            sub="todos os títulos pagos pelos sacados"
          />
        </div>
      )}

      {/* Table */}
      <EstadoDaLista carga={cargaContratos} temDados={cargaContratos.atualizadoEm !== null} oQue="os contratos" onTentarDeNovo={loadContracts} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
      {/* Tabela: a casca das listas, SEM overflow de rolagem (o cabeçalho fixa no <main>, guia §3.6). */}
      <CascaDaLista>
          <TabelaDaLista>
            {activeTab === 'emprestimos' ? (
              <CabecalhoDaLista>
                <ColunaDaLista rotulo="Contrato" largura="w-72" />
                <ColunaDaLista rotulo="Instituição" />
                <ColunaDaLista rotulo="Restante" largura="w-56" alinhamento="direita" />
                <ColunaDaLista rotulo="Próxima parcela" largura="w-56" />
                <ColunaDaLista rotulo="Progresso" largura="w-40" alinhamento="centro" />
                <ColunaDaLista rotulo="Status" largura="w-32" alinhamento="centro" />
                <ColunaDaLista rotulo={<span className="sr-only">Ações</span>} largura="w-20" />
              </CabecalhoDaLista>
            ) : (
              <CabecalhoDaLista>
                <ColunaDaLista rotulo="Borderô" />
                <ColunaDaLista rotulo="Data" largura="w-28" />
                <ColunaDaLista rotulo="Valor Face" largura="w-36" alinhamento="direita" />
                <ColunaDaLista rotulo="Líquido" largura="w-36" alinhamento="direita" />
                <ColunaDaLista rotulo="IOF + Tarifa" largura="w-32" alinhamento="direita" />
                <ColunaDaLista rotulo="Liquidação" largura="w-40" alinhamento="centro" />
                <ColunaDaLista rotulo="Status" largura="w-44" alinhamento="centro" />
                <ColunaDaLista rotulo={<span className="sr-only">Ações</span>} largura="w-20" />
              </CabecalhoDaLista>
            )}
            <tbody className="divide-y divide-slate-800">
              {filteredContracts.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500 text-sm">
                  {activeTab === 'emprestimos' ? 'Nenhum contrato de empréstimo cadastrado' : 'Nenhum borderô importado'}
                </td></tr>
              ) : filteredContracts.map((c: any) => {
                const isDesconto = c.type === 'desconto_duplicatas';
                const summaryContract = resumoDoContrato(c.id);
                // Progresso vem do backend nos DOIS tipos. Recalcular aqui com
                // `paidInstallments / totalInstallments` ignorava a carência e
                // travava contrato com carência abaixo de 100%: o Pronampe
                // quitado marcaria 37/48 = 77%.
                const progress = Number(summaryContract?.progress) || 0;

                if (isDesconto) {
                  const iof = Number(c.iofAmount) || 0;
                  const tarifa = Number(c.tarifaAmount) || 0;
                  const estBordero = estadoBordero(c, summaryContract);
                  return (
                    <LinhaDaLista key={c.id}>
                      <td className={CELULA}>
                        <div className="text-sm font-mono text-slate-300">{c.contractNumber}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{c.discountedReceivables?.length || 0} títulos</div>
                      </td>
                      <td className={`${CELULA} text-sm text-slate-300`}>{fmtDate(c.startDate)}</td>
                      {/* E1: os valores em branco (o líquido era verde; o custo, vermelho). */}
                      <CelulaDinheiro valor={c.faceValue || c.originalAmount} />
                      <CelulaDinheiro valor={c.netAmountReceived} />
                      <CelulaDinheiro valor={iof + tarifa} />
                      <td className={CELULA}>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-700 rounded-full h-2 min-w-[60px]">
                            <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
                          </div>
                          <span className="text-xs text-slate-400 whitespace-nowrap">{progress}%</span>
                        </div>
                      </td>
                      <td className={`${CELULA} text-center`}>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${estBordero.chip}`}>
                          {estBordero.rotulo}
                        </span>
                        <div className="text-[11px] text-slate-500 mt-1">{estBordero.detalhe}</div>
                      </td>
                      {/* D1: a lixeira e o upload saíram da linha; tudo no ⋯, o Excluir em perigo e separado. */}
                      <CelulaAcoes
                        rotuloDoMenu="Ações do borderô"
                        itens={[
                          { label: <><Icon name="eye" className="w-4 h-4" /> Ver detalhes</>, onClick: () => openDetail(c) },
                          { label: <><Icon name="upload" className="w-4 h-4" /> Upload do borderô</>, onClick: () => setUploadModal(c) },
                          { label: <><Icon name="trash" className="w-4 h-4" /> Excluir</>, onClick: () => setConfirmDelete(c), danger: true, separadoAntes: true },
                        ]}
                      />
                    </LinhaDaLista>
                  );
                }

                const prox = calcProximaParcela(c, summaryContract);
                const pagas = Number(summaryContract?.paidInstallments ?? c.paidInstallments) || 0;
                const prestacoes = Number(summaryContract?.payableInstallments) || Number(c.totalInstallments) || 0;
                const restante = Number(summaryContract?.remaining) || 0;
                const daFonte = summaryContract?.balanceSource === 'fonte';

                return (
                  <LinhaDaLista key={c.id} className="align-top">
                    <td className={CELULA}>
                      <div className="text-sm font-mono text-slate-300">{c.contractNumber}</div>
                      <div className="text-xs text-slate-500 mt-0.5 max-w-[220px] truncate" title={c.description}>{c.description}</div>
                    </td>
                    <td className={`${CELULA} text-sm text-slate-300`}>{c.supplier?.name || c.bankAccount?.bankName || TRACO}</td>
                    {/* E1: o restante em branco, com a origem do saldo embaixo. */}
                    <CelulaDinheiro valor={restante}>
                      {/* Com âncora, o valor exibido pode diferir do que o credor
                          mostrou — porque parcelas foram pagas depois da consulta.
                          Dizer quantas evita a pergunta "por que está diferente do
                          banco?" e deixa claro quando vale reconsultar. A frase quebra linha: a célula de dinheiro não
                          quebra, e a origem do saldo vazava para a coluna do lado (visto na prévia). */}
                      <div className="text-xs text-slate-500 mt-0.5 whitespace-normal">
                        {daFonte
                          ? `credor em ${fmtDate(summaryContract.sourceBalanceDate)}${
                              Number(summaryContract.paidSinceSource) > 0
                                ? `, ${summaryContract.paidSinceSource} parcela${Number(summaryContract.paidSinceSource) > 1 ? 's' : ''} depois`
                                : ''
                            }`
                          : `de R$ ${fmt(c.originalAmount)}`}
                      </div>
                    </CelulaDinheiro>
                    <td className={CELULA}>
                      {!prox || prox.estado === 'quitado' ? (
                        <span className="text-sm text-slate-500">{TRACO}</span>
                      ) : (
                        <>
                          <div className="text-sm text-white whitespace-nowrap">
                            {fmtDate(prox.dueDate)} <span className="text-slate-500">·</span> R$ {fmt(prox.amount)}
                          </div>
                          <div className="text-xs mt-0.5 whitespace-nowrap">
                            {prox.estado === 'carencia' && (
                              <span className="text-slate-400">Em carência, 1a prestação nesta data</span>
                            )}
                            {prox.estado === 'vencida' && (() => {
                              const l = leituraDa(c.id);
                              if (l === 'lendo') return <span className="text-slate-400">Conferindo…</span>;
                              return (
                                <span className={l.estado === 'vencida' ? 'text-red-400 font-medium' : l.estado === 'paga' ? 'text-amber-300' : 'text-slate-400'}>
                                  {ROTULO_DA_PARCELA[l.estado]}
                                </span>
                              );
                            })()}
                            {prox.estado === 'a_vencer' && (
                              <span className="text-slate-400">A debitar</span>
                            )}
                          </div>
                        </>
                      )}
                    </td>
                    <td className={CELULA}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-700 rounded-full h-2 min-w-[60px]">
                          <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap">{progress}%</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {pagas}/{prestacoes} prestações
                      </div>
                    </td>
                    <td className={`${CELULA} text-center`}>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusChip[c.status] || statusChipFallback}`}>
                        {statusLabels[c.status] || c.status}
                      </span>
                    </td>
                    {/* D7 (11/10/2026): era um menu `fixed` posicionado à mão; agora o ⋯ da CelulaAcoes (ancoragem fixa). */}
                    <CelulaAcoes
                      rotuloDoMenu="Ações do contrato"
                      itens={[
                        { label: <><Icon name="eye" className="w-4 h-4" /> Ver cronograma</>, onClick: () => openDetail(c) },
                        { label: <><Icon name="list" className="w-4 h-4" /> Ver parcelas em Despesas</>, onClick: () => onOpenExpenses?.(c.contractNumber) },
                        { label: <><Icon name="edit" className="w-4 h-4" /> Editar</>, onClick: () => { setEditingContract(c); setFormModal('edit'); } },
                        { label: <><Icon name="refresh" className="w-4 h-4" /> Sincronizar parcelas</>, onClick: () => handleSync(c.id) },
                        { label: <><Icon name="upload" className="w-4 h-4" /> Anexar contrato PDF</>, onClick: () => setUploadModal(c) },
                        ...(c.attachmentS3Key ? [{ label: <><Icon name="download" className="w-4 h-4" /> Ver contrato</>, onClick: () => handleDownloadContract(c) }] : []),
                        { label: <><Icon name="trash" className="w-4 h-4" /> Excluir</>, onClick: () => setConfirmDelete(c), danger: true, separadoAntes: true },
                      ]}
                    />
                  </LinhaDaLista>
                );
              })}
            </tbody>
          </TabelaDaLista>
      </CascaDaLista>
      </EstadoDaLista>

      {/* Modals */}
      {formModal && <ContractForm ctx={modalCtx} onClose={() => setFormModal(null)} contract={formModal === 'edit' ? editingContract : undefined} />}
      {detailModal && <DetailModal ctx={modalCtx} contract={detailModal} onClose={() => { setDetailModal(null); setSchedule([]); }} />}
      {uploadModal && <UploadModal ctx={modalCtx} contract={uploadModal} onClose={() => setUploadModal(null)} />}
      {borderoModal && <BorderoImportModal ctx={modalCtx} onClose={() => setBorderoModal(false)} />}
    </div>
  );
}
